import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Plus, Trash2, GripVertical, ChevronDown, ChevronRight, 
  Globe, Layout, FileText, Image, CheckCircle2, Circle,
  Folder, File, Settings, Code, Mic
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import AudioRecorder from '../ui/AudioRecorder';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// =============================================
// WEBSITE BRIEF FORM - Formulaire de brief pour sites web
// Inclut: fonctionnalités, technologie, arborescence, contenus
// =============================================

// Fonctionnalités de site web prédéfinies
const WEBSITE_FEATURES = {
  essential: {
    title: 'Fonctionnalités essentielles',
    features: [
      { id: 'responsive', label: 'Design responsive (mobile/tablette)', default: true },
      { id: 'contact_form', label: 'Formulaire de contact', default: true },
      { id: 'seo', label: 'Optimisation SEO de base', default: true },
      { id: 'analytics', label: 'Google Analytics / Statistiques', default: true },
      { id: 'ssl', label: 'Certificat SSL (HTTPS)', default: true },
    ]
  },
  content: {
    title: 'Gestion de contenu',
    features: [
      { id: 'blog', label: 'Blog / Actualités' },
      { id: 'gallery', label: 'Galerie photos / Portfolio' },
      { id: 'video', label: 'Intégration vidéos' },
      { id: 'testimonials', label: 'Témoignages clients' },
      { id: 'faq', label: 'FAQ / Questions fréquentes' },
      { id: 'team', label: 'Page équipe / Trombinoscope' },
    ]
  },
  ecommerce: {
    title: 'E-commerce',
    features: [
      { id: 'product_catalog', label: 'Catalogue produits' },
      { id: 'shopping_cart', label: 'Panier d\'achat' },
      { id: 'online_payment', label: 'Paiement en ligne (CB, PayPal...)' },
      { id: 'stock_management', label: 'Gestion des stocks' },
      { id: 'order_tracking', label: 'Suivi de commandes' },
      { id: 'promo_codes', label: 'Codes promo / Réductions' },
    ]
  },
  interactive: {
    title: 'Fonctionnalités interactives',
    features: [
      { id: 'user_accounts', label: 'Espace membre / Comptes utilisateurs' },
      { id: 'newsletter', label: 'Inscription newsletter' },
      { id: 'booking', label: 'Réservation en ligne / Prise de RDV' },
      { id: 'quote_request', label: 'Demande de devis en ligne' },
      { id: 'live_chat', label: 'Chat en direct' },
      { id: 'search', label: 'Moteur de recherche interne' },
    ]
  },
  social: {
    title: 'Réseaux sociaux & Partage',
    features: [
      { id: 'social_links', label: 'Liens vers réseaux sociaux' },
      { id: 'social_feed', label: 'Flux Instagram / Facebook intégré' },
      { id: 'share_buttons', label: 'Boutons de partage' },
      { id: 'google_maps', label: 'Carte Google Maps' },
    ]
  },
  advanced: {
    title: 'Fonctionnalités avancées',
    features: [
      { id: 'multilingual', label: 'Site multilingue' },
      { id: 'crm_integration', label: 'Intégration CRM' },
      { id: 'api', label: 'API / Intégrations tierces' },
      { id: 'custom_animations', label: 'Animations personnalisées' },
      { id: 'accessibility', label: 'Accessibilité (RGAA/WCAG)' },
    ]
  }
};

// Pages types pour l'arborescence
const DEFAULT_PAGES = [
  { id: 'home', name: 'Accueil', required: true },
  { id: 'about', name: 'À propos' },
  { id: 'services', name: 'Services / Prestations' },
  { id: 'portfolio', name: 'Réalisations / Portfolio' },
  { id: 'contact', name: 'Contact' },
  { id: 'legal', name: 'Mentions légales', required: true },
  { id: 'privacy', name: 'Politique de confidentialité', required: true },
];

// Contenus à fournir selon les pages/fonctionnalités
const CONTENT_REQUIREMENTS = {
  home: [
    { id: 'home_hero_text', label: 'Texte d\'accroche (hero)', type: 'text' },
    { id: 'home_hero_image', label: 'Image principale (hero)', type: 'image' },
    { id: 'home_intro', label: 'Texte de présentation', type: 'text' },
  ],
  about: [
    { id: 'about_text', label: 'Texte de présentation entreprise', type: 'text' },
    { id: 'about_history', label: 'Historique / Parcours', type: 'text' },
    { id: 'about_photos', label: 'Photos de l\'entreprise / équipe', type: 'image' },
  ],
  services: [
    { id: 'services_list', label: 'Liste des services avec descriptions', type: 'text' },
    { id: 'services_images', label: 'Images / Icônes pour chaque service', type: 'image' },
    { id: 'services_pricing', label: 'Tarifs (si applicable)', type: 'text' },
  ],
  portfolio: [
    { id: 'portfolio_projects', label: 'Descriptions des projets', type: 'text' },
    { id: 'portfolio_images', label: 'Photos / Visuels des réalisations', type: 'image' },
  ],
  contact: [
    { id: 'contact_info', label: 'Coordonnées complètes', type: 'text' },
    { id: 'contact_hours', label: 'Horaires d\'ouverture', type: 'text' },
  ],
  blog: [
    { id: 'blog_categories', label: 'Catégories d\'articles', type: 'text' },
    { id: 'blog_articles', label: 'Premiers articles (minimum 3)', type: 'text' },
  ],
  team: [
    { id: 'team_members', label: 'Noms, postes et descriptions', type: 'text' },
    { id: 'team_photos', label: 'Photos individuelles', type: 'image' },
  ],
  testimonials: [
    { id: 'testimonials_texts', label: 'Témoignages clients', type: 'text' },
    { id: 'testimonials_photos', label: 'Photos clients (optionnel)', type: 'image' },
  ],
  product_catalog: [
    { id: 'products_list', label: 'Liste produits avec descriptions', type: 'text' },
    { id: 'products_photos', label: 'Photos produits (haute qualité)', type: 'image' },
    { id: 'products_prices', label: 'Prix et références', type: 'text' },
  ],
  gallery: [
    { id: 'gallery_images', label: 'Images haute résolution', type: 'image' },
    { id: 'gallery_captions', label: 'Légendes / Descriptions', type: 'text' },
  ],
  general: [
    { id: 'logo', label: 'Logo (vectoriel de préférence)', type: 'image', required: true },
    { id: 'brand_colors', label: 'Couleurs de la charte graphique', type: 'text' },
    { id: 'brand_fonts', label: 'Polices de la charte (si existante)', type: 'text' },
    { id: 'favicon', label: 'Favicon / Icône du site', type: 'image' },
  ]
};

// Composant pour une page draggable
const SortablePageItem = ({ page, onUpdate, onRemove, onAddSubpage, onUpdateSubpage, onRemoveSubpage, theme }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: page.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="space-y-2">
      <div className={cn(
        "flex items-center gap-2 p-2 rounded-lg border bg-white",
        isDragging && "shadow-lg ring-2 ring-offset-2",
        page.required && "bg-slate-50"
      )}
      style={isDragging ? { borderColor: theme?.colors?.accent } : {}}
      >
        <div 
          {...attributes} 
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-100 rounded"
        >
          <GripVertical className="w-4 h-4 text-slate-400" />
        </div>
        <Folder className="w-4 h-4 text-blue-500 flex-shrink-0" />
        <Input
          value={page.name}
          onChange={(e) => onUpdate(page.id, e.target.value)}
          className="flex-1 h-8 text-sm"
          placeholder="Nom de la page"
          disabled={page.required}
        />
        {page.required && (
          <Badge variant="secondary" className="text-xs">Obligatoire</Badge>
        )}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onAddSubpage(page.id)}
          className="h-7 w-7 p-0"
        >
          <Plus className="w-3 h-3" />
        </Button>
        {!page.required && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onRemove(page.id)}
            className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        )}
      </div>
      
      {/* Sous-pages */}
      {page.subpages && page.subpages.length > 0 && (
        <div className="ml-8 space-y-1">
          {page.subpages.map((subpage) => (
            <div key={subpage.id} className="flex items-center gap-2 p-2 rounded-lg border bg-slate-50">
              <div className="w-5" /> {/* Spacer for alignment */}
              <File className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <Input
                value={subpage.name}
                onChange={(e) => onUpdateSubpage(page.id, subpage.id, e.target.value)}
                className="flex-1 h-8 text-sm"
                placeholder="Nom de la sous-page"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onRemoveSubpage(page.id, subpage.id)}
                className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const WebsiteBriefForm = ({ 
  projectId, 
  initialData = null,
  onSuccess, 
  onCancel, 
  theme,
  isAgency = false 
}) => {
  // États du formulaire
  const [technology, setTechnology] = useState(initialData?.technology || '');
  const [siteType, setSiteType] = useState(initialData?.site_type || '');
  const [objective, setObjective] = useState(initialData?.objective || '');
  const [targetAudience, setTargetAudience] = useState(initialData?.target_audience || '');
  const [existingSite, setExistingSite] = useState(initialData?.existing_site || '');
  const [inspirations, setInspirations] = useState(initialData?.inspirations || '');
  
  // Fonctionnalités sélectionnées
  const [selectedFeatures, setSelectedFeatures] = useState(
    initialData?.features || 
    Object.values(WEBSITE_FEATURES).flatMap(cat => 
      cat.features.filter(f => f.default).map(f => f.id)
    )
  );
  
  // Arborescence des pages
  const [pages, setPages] = useState(
    initialData?.pages || DEFAULT_PAGES.filter(p => p.required).map(p => ({
      ...p,
      subpages: []
    }))
  );
  
  // Contenus à fournir
  const [contentRequirements, setContentRequirements] = useState(
    initialData?.content_requirements || []
  );
  
  // Contenus reçus (pour l'agence)
  const [receivedContent, setReceivedContent] = useState(
    initialData?.received_content || {}
  );
  
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState(['essential']);

  // Calculer les contenus requis en fonction des pages et fonctionnalités
  useEffect(() => {
    const requirements = [...CONTENT_REQUIREMENTS.general];
    
    // Ajouter les contenus selon les pages
    pages.forEach(page => {
      const pageRequirements = CONTENT_REQUIREMENTS[page.id];
      if (pageRequirements) {
        requirements.push(...pageRequirements);
      }
    });
    
    // Ajouter les contenus selon les fonctionnalités
    selectedFeatures.forEach(featureId => {
      const featureRequirements = CONTENT_REQUIREMENTS[featureId];
      if (featureRequirements) {
        requirements.push(...featureRequirements);
      }
    });
    
    // Dédupliquer par id
    const uniqueRequirements = requirements.filter((req, index, self) => 
      index === self.findIndex(r => r.id === req.id)
    );
    
    setContentRequirements(uniqueRequirements);
  }, [pages, selectedFeatures]);

  // Toggle une fonctionnalité
  const toggleFeature = (featureId) => {
    setSelectedFeatures(prev => 
      prev.includes(featureId) 
        ? prev.filter(f => f !== featureId)
        : [...prev, featureId]
    );
  };

  // Ajouter une page
  const addPage = () => {
    const newPage = {
      id: `page_${Date.now()}`,
      name: 'Nouvelle page',
      subpages: []
    };
    setPages([...pages, newPage]);
  };

  // Supprimer une page
  const removePage = (pageId) => {
    setPages(pages.filter(p => p.id !== pageId && !p.required));
  };

  // Modifier une page
  const updatePage = (pageId, name) => {
    setPages(pages.map(p => p.id === pageId ? { ...p, name } : p));
  };

  // Ajouter une sous-page
  const addSubpage = (parentId) => {
    setPages(pages.map(p => {
      if (p.id === parentId) {
        return {
          ...p,
          subpages: [...(p.subpages || []), { id: `subpage_${Date.now()}`, name: 'Sous-page' }]
        };
      }
      return p;
    }));
  };

  // Supprimer une sous-page
  const removeSubpage = (parentId, subpageId) => {
    setPages(pages.map(p => {
      if (p.id === parentId) {
        return {
          ...p,
          subpages: p.subpages.filter(sp => sp.id !== subpageId)
        };
      }
      return p;
    }));
  };

  // Modifier une sous-page
  const updateSubpage = (parentId, subpageId, name) => {
    setPages(pages.map(p => {
      if (p.id === parentId) {
        return {
          ...p,
          subpages: p.subpages.map(sp => sp.id === subpageId ? { ...sp, name } : sp)
        };
      }
      return p;
    }));
  };

  // Toggle contenu reçu
  const toggleReceivedContent = (contentId) => {
    setReceivedContent(prev => ({
      ...prev,
      [contentId]: !prev[contentId]
    }));
  };

  // Compter les pages totales
  const totalPages = pages.reduce((acc, p) => acc + 1 + (p.subpages?.length || 0), 0);

  // DnD sensors configuration
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end for reordering pages
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setPages((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // Soumettre le formulaire
  const handleSubmit = async () => {
    if (!technology) {
      toast.error('Veuillez sélectionner une technologie');
      return;
    }
    if (!siteType) {
      toast.error('Veuillez sélectionner un type de site');
      return;
    }

    setSaving(true);
    try {
      const briefData = {
        project_id: projectId,
        brief_type: 'website_advanced',
        technology,
        site_type: siteType,
        objective,
        target_audience: targetAudience,
        existing_site: existingSite,
        inspirations,
        features: selectedFeatures,
        pages,
        content_requirements: contentRequirements,
        received_content: receivedContent,
        total_pages: totalPages
      };

      await onSuccess(briefData);
      toast.success('Brief enregistré avec succès !');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => 
      prev.includes(sectionKey) 
        ? prev.filter(s => s !== sectionKey)
        : [...prev, sectionKey]
    );
  };

  return (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
      {/* Section 1: Informations générales */}
      <Card className="border-l-4" style={{ borderLeftColor: theme?.colors?.primary }}>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Globe className="w-5 h-5" style={{ color: theme?.colors?.accent }} />
            Informations générales
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-medium">Technologie *</Label>
              <Select value={technology} onValueChange={setTechnology}>
                <SelectTrigger>
                  <SelectValue placeholder="Choisir la technologie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="wordpress">
                    <div className="flex items-center gap-2">
                      <Code className="w-4 h-4" />
                      WordPress (CMS)
                    </div>
                  </SelectItem>
                  <SelectItem value="react">
                    <div className="flex items-center gap-2">
                      <Code className="w-4 h-4" />
                      React (Application web)
                    </div>
                  </SelectItem>
                  <SelectItem value="shopify">
                    <div className="flex items-center gap-2">
                      <Code className="w-4 h-4" />
                      Shopify (E-commerce)
                    </div>
                  </SelectItem>
                  <SelectItem value="custom">
                    <div className="flex items-center gap-2">
                      <Code className="w-4 h-4" />
                      Sur mesure
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="font-medium">Type de site *</Label>
              <Select value={siteType} onValueChange={setSiteType}>
                <SelectTrigger>
                  <SelectValue placeholder="Choisir le type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vitrine">Site vitrine</SelectItem>
                  <SelectItem value="ecommerce">E-commerce</SelectItem>
                  <SelectItem value="blog">Blog / Magazine</SelectItem>
                  <SelectItem value="portfolio">Portfolio</SelectItem>
                  <SelectItem value="landing">Landing page</SelectItem>
                  <SelectItem value="webapp">Application web</SelectItem>
                  <SelectItem value="corporate">Site corporate</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="font-medium flex items-center gap-2">
              <Mic className="w-4 h-4" style={{ color: theme?.colors?.accent }} />
              Objectif principal du site
            </Label>
            <Textarea
              value={objective}
              onChange={(e) => setObjective(e.target.value)}
              placeholder="Décrivez l'objectif principal du site (générer des leads, vendre des produits, informer...)"
              rows={3}
            />
            <AudioRecorder
              onTranscription={(text) => setObjective(prev => prev ? `${prev} ${text}` : text)}
              placeholder="Dictez l'objectif de votre site"
              theme={theme}
            />
          </div>

          <div className="space-y-2">
            <Label className="font-medium flex items-center gap-2">
              <Mic className="w-4 h-4" style={{ color: theme?.colors?.accent }} />
              Public cible
            </Label>
            <Textarea
              value={targetAudience}
              onChange={(e) => setTargetAudience(e.target.value)}
              placeholder="Décrivez votre audience cible (âge, profession, centres d'intérêt...)"
              rows={2}
            />
            <AudioRecorder
              onTranscription={(text) => setTargetAudience(prev => prev ? `${prev} ${text}` : text)}
              placeholder="Dictez la description de votre cible"
              theme={theme}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-medium">Site actuel (si refonte)</Label>
              <Input
                value={existingSite}
                onChange={(e) => setExistingSite(e.target.value)}
                placeholder="https://www.exemple.com"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-medium">Sites d'inspiration</Label>
              <Input
                value={inspirations}
                onChange={(e) => setInspirations(e.target.value)}
                placeholder="URLs de sites qui vous inspirent"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 2: Fonctionnalités */}
      <Card className="border-l-4" style={{ borderLeftColor: theme?.colors?.accent }}>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Settings className="w-5 h-5" style={{ color: theme?.colors?.accent }} />
            Fonctionnalités du site
          </CardTitle>
          <CardDescription>
            Sélectionnez les fonctionnalités souhaitées pour votre site
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {Object.entries(WEBSITE_FEATURES).map(([key, category]) => (
            <div key={key} className="border rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => toggleSection(key)}
                className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                <span className="font-medium text-sm">{category.title}</span>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-xs">
                    {category.features.filter(f => selectedFeatures.includes(f.id)).length}/{category.features.length}
                  </Badge>
                  {expandedSections.includes(key) ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </div>
              </button>
              {expandedSections.includes(key) && (
                <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                  {category.features.map(feature => (
                    <label
                      key={feature.id}
                      className={cn(
                        "flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors",
                        selectedFeatures.includes(feature.id) 
                          ? "bg-blue-50 border border-blue-200" 
                          : "hover:bg-slate-50"
                      )}
                    >
                      <Checkbox
                        checked={selectedFeatures.includes(feature.id)}
                        onCheckedChange={() => toggleFeature(feature.id)}
                      />
                      <span className="text-sm">{feature.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Section 3: Arborescence du site */}
      <Card className="border-l-4" style={{ borderLeftColor: theme?.colors?.primary }}>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Layout className="w-5 h-5" style={{ color: theme?.colors?.accent }} />
            Arborescence du site
          </CardTitle>
          <CardDescription>
            Glissez-déposez pour réorganiser les pages ({totalPages} page{totalPages > 1 ? 's' : ''})
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={pages.map(p => p.id)}
              strategy={verticalListSortingStrategy}
            >
              {pages.map((page) => (
                <SortablePageItem
                  key={page.id}
                  page={page}
                  onUpdate={updatePage}
                  onRemove={removePage}
                  onAddSubpage={addSubpage}
                  onUpdateSubpage={updateSubpage}
                  onRemoveSubpage={removeSubpage}
                  theme={theme}
                />
              ))}
            </SortableContext>
          </DndContext>
          
          <Button
            type="button"
            variant="outline"
            onClick={addPage}
            className="w-full"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter une page
          </Button>
        </CardContent>
      </Card>

      {/* Section 4: Contenus à fournir */}
      <Card className="border-l-4" style={{ borderLeftColor: theme?.colors?.alert || '#f59e0b' }}>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileText className="w-5 h-5" style={{ color: theme?.colors?.alert || '#f59e0b' }} />
            Contenus à fournir par le client
          </CardTitle>
          <CardDescription>
            Liste des éléments que le client devra fournir pour la réalisation du site
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {contentRequirements.map(content => (
              <div 
                key={content.id}
                className={cn(
                  "flex items-center justify-between p-3 rounded-lg border transition-colors",
                  receivedContent[content.id] ? "bg-green-50 border-green-200" : "bg-white"
                )}
              >
                <div className="flex items-center gap-3">
                  {content.type === 'image' ? (
                    <Image className="w-4 h-4 text-purple-500" />
                  ) : (
                    <FileText className="w-4 h-4 text-blue-500" />
                  )}
                  <span className="text-sm">
                    {content.label}
                    {content.required && <span className="text-red-500 ml-1">*</span>}
                  </span>
                </div>
                
                {isAgency && (
                  <button
                    type="button"
                    onClick={() => toggleReceivedContent(content.id)}
                    className="flex items-center gap-2"
                  >
                    {receivedContent[content.id] ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : (
                      <Circle className="w-5 h-5 text-slate-300" />
                    )}
                    <span className={cn(
                      "text-xs",
                      receivedContent[content.id] ? "text-green-600" : "text-slate-400"
                    )}>
                      {receivedContent[content.id] ? 'Reçu' : 'En attente'}
                    </span>
                  </button>
                )}
              </div>
            ))}
          </div>
          
          {/* Résumé */}
          <div className="mt-4 p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">
                Contenus reçus : {Object.values(receivedContent).filter(Boolean).length} / {contentRequirements.length}
              </span>
              <div className="h-2 w-32 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-500 transition-all"
                  style={{ 
                    width: `${(Object.values(receivedContent).filter(Boolean).length / contentRequirements.length) * 100}%` 
                  }}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>
          Annuler
        </Button>
        <Button 
          onClick={handleSubmit}
          disabled={saving}
          style={{ backgroundColor: theme?.colors?.accent }}
        >
          {saving ? 'Enregistrement...' : 'Enregistrer le brief'}
        </Button>
      </div>
    </div>
  );
};

export default WebsiteBriefForm;
